import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail, MessageSquareHeart, Send, Star } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Field } from "@/pages/Login";

const Area = ({ id, label, ...p }) => (
  <label className="block space-y-1.5"><span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</span>
    <textarea data-testid={id} {...p} className="min-h-[140px] w-full rounded-lg border border-slate-300 px-3 py-2.5 text-slate-900 outline-none transition-[border-color,box-shadow] focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/30" /></label>
);

export default function Contact() {
  const { user } = useAuth();
  const blank = { name: user?.name || "", email: user?.email || "", phone: user?.phone_number || "", subject: "", message: "" };
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false), [err, setErr] = useState(""), [sent, setSent] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (f.message.trim().length < 10) return setErr("Please write at least 10 characters.");
    setBusy(true);
    try { await api.post("/contact", f); setSent(true); setF(blank); toast.success("Message sent. We'll reply by email."); }
    catch (x) { setErr(errMsg(x)); } finally { setBusy(false); }
  };
  return (
    <div className="mx-auto grid max-w-6xl gap-10 px-4 py-12 sm:px-8 lg:grid-cols-[1fr_560px] lg:py-16">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-indigo-300">Contact us</p>
        <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl">We're here to help.</h1>
        <p className="mt-4 max-w-md text-slate-400">Questions about the exam, your Practice Pass or a technical problem? Send us a note and we'll reply by email, usually within one business day.</p>
        <a href="mailto:support@lexversant.ca" data-testid="contact-email-link" className="mt-8 inline-flex items-center gap-2 rounded-full border border-slate-700 px-4 py-2 text-sm text-slate-200 transition-colors hover:bg-slate-800"><Mail className="h-4 w-4" />support@lexversant.ca</a>
      </div>
      <form onSubmit={submit} data-testid="contact-form" className="space-y-4 rounded-2xl bg-white p-6 shadow-2xl shadow-slate-950/50 sm:p-8">
        {sent && <p data-testid="contact-success" className="rounded-lg bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700">Thanks! Your message has been received.</p>}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field id="contact-name-input" label="Name" value={f.name} onChange={set("name")} required maxLength={120} />
          <Field id="contact-email-input" label="Email" type="email" value={f.email} onChange={set("email")} required />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field id="contact-phone-input" label="Phone (optional)" type="tel" value={f.phone} onChange={set("phone")} maxLength={25} />
          <Field id="contact-subject-input" label="Subject" value={f.subject} onChange={set("subject")} required maxLength={150} />
        </div>
        <Area id="contact-message-input" label="Message" value={f.message} onChange={set("message")} required maxLength={4000} />
        {err && <p data-testid="contact-error" className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{err}</p>}
        <button data-testid="contact-submit-btn" disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-full bg-indigo-600 py-3 text-sm font-bold text-white transition-colors hover:bg-indigo-700 disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}Send message</button>
      </form>
    </div>
  );
}

const CATS = ["Content", "Audio & speech", "Scoring", "Design", "Bug", "Other"];

export const FeedbackModal = ({ open, onOpenChange }) => {
  const [rating, setRating] = useState(0), [hover, setHover] = useState(0);
  const [category, setCategory] = useState("Content"), [text, setText] = useState("");
  const [busy, setBusy] = useState(false), [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (!rating) return setErr("Pick a rating from 1 to 5 stars.");
    setBusy(true);
    try { await api.post("/feedback", { rating, category, feedback_text: text }); toast.success("Thanks for your feedback!"); setRating(0); setText(""); onOpenChange(false); }
    catch (x) { setErr(errMsg(x)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="feedback-modal" className="max-w-[calc(100%-2rem)] rounded-2xl bg-white sm:max-w-md">
        <DialogHeader>
          <div className="mb-1 grid h-11 w-11 place-items-center rounded-full bg-indigo-50 text-indigo-600"><MessageSquareHeart className="h-5 w-5" /></div>
          <DialogTitle className="font-display text-2xl">Share your feedback</DialogTitle>
          <DialogDescription>Tell us what's working and what we should improve.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="flex gap-1" onMouseLeave={() => setHover(0)} data-testid="feedback-rating">
            {[1, 2, 3, 4, 5].map((n) => <button type="button" key={n} data-testid={`feedback-star-${n}`} onMouseEnter={() => setHover(n)} onClick={() => setRating(n)} aria-label={`${n} stars`} className="rounded p-1 transition-transform hover:scale-110"><Star className={`h-7 w-7 ${(hover || rating) >= n ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} /></button>)}
          </div>
          <div className="flex flex-wrap gap-2" data-testid="feedback-categories">
            {CATS.map((c) => <button type="button" key={c} data-testid={`feedback-cat-${c.split(" ")[0].toLowerCase()}`} onClick={() => setCategory(c)} className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${category === c ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{c}</button>)}
          </div>
          <Area id="feedback-text-input" label="Your thoughts" value={text} onChange={(e) => setText(e.target.value)} required minLength={3} maxLength={4000} />
          {err && <p data-testid="feedback-error" className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{err}</p>}
          <button data-testid="feedback-submit-btn" disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-full bg-indigo-600 py-3 text-sm font-bold text-white transition-colors hover:bg-indigo-700 disabled:opacity-60">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Send feedback</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
